import React, { useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import CONFIG from '~/config'
import { ROUTES } from '~/pages/constants'
import {
  EXPERIMENTS,
  APPLE_AUTH_TYPES,
  GOOGLE_AUTH_TYPES,
  WELCOME5_PROMOCODE,
} from '~/constants'
import { useReCAPTCHAv2 } from '~/features/ReCaptcha/hooks/useReCAPTCHAv2'
import { useRegisterUserMutation, useLazyFetchUserContextQuery } from '~/store/api'
import { setIsAuthorized } from '~/store/modules/auth'
import { setUserToken } from '~/utils'
import {
  identifySegmentUser,
  MEMBER_REGISTERED,
  PROMO_CODE_REDEEMED,
  getSegmentUserContext,
  USER_ACTIVATED
} from '~/services/segment'
import { useExperiment } from '~/hooks/useExperiment'
import { useSegmentEvents } from '~/hooks/useSegmentEvents'
import SignInWithGoogleButton from '~/services/google/SignIn/Button'
import SignInWithAppleButton from '~/services/apple/SignIn/Button'
import {
  signUpFormValidationSchema,
  getSignUpFormDefaultValues,
} from '~/pages/Signup/SignUpV2/Registration/utils'
import FormTextField from '~/components/form/TextField'
import FormEmailField from '~/components/form/EmailField'
import FormPasswordField from '~/components/form/PasswordField'
import Link from '~/components/ui/Link'
import CTAButton from '~/components/ui/Buttons/CTAButton'
import ReCaptchaV3Terms from '~/features/ReCaptcha/ReCaptchaV3Terms'
import { getReCaptcha3Token, RECAPTCHA_V3, isReCaptchaV3Enabled } from '~/features/ReCaptcha/utils'

const SignUpV3RegistrationForm = props => {
  const {
    promoCode,
    submitBtnText,
  } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { enabled: isAppleSignInEnabled } = useExperiment(EXPERIMENTS.APPLE_SIGN_IN, false)
  const { enabled: isPrimeGuaranteeEnabled } = useExperiment(EXPERIMENTS.PRIME_3_MONTH_GUARANTEE, false)
  const { enabled: isShutdownCreateAccountEnabled } = useExperiment(EXPERIMENTS.SHUTDOWN_CREATE_ACCOUNT)

  const [registerUser, registerUserApiResult] = useRegisterUserMutation()
  const [fetchUserContext] = useLazyFetchUserContextQuery()

  const [trackMemberRegistration, trackPromoCodeRedeemed] = useSegmentEvents([MEMBER_REGISTERED, PROMO_CODE_REDEEMED, USER_ACTIVATED])

  const {
    control,
    formState: {
      isValid,
      isSubmitting,
      errors,
    },
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(signUpFormValidationSchema),
    defaultValues: getSignUpFormDefaultValues(useSearchParams()),
  })

  useEffect(() => {
    reset(formValues => ({
      ...formValues,
      promoCode
    }))
  }, [promoCode, reset])

  const {
    ReCAPTCHA,
    onRecaptchaReset,
  } = useReCAPTCHAv2(errors.recaptchaToken, reset, setError)

  const onSubmit = useCallback(async formData => {
    if (isReCaptchaV3Enabled) {
      formData.recaptchaToken = await getReCaptcha3Token()
      formData.recaptchaVersion = RECAPTCHA_V3
    }

    formData.withCreditScoreGuarantee = isPrimeGuaranteeEnabled

    const registerResponse = await registerUser(formData)

    if (!registerResponse.error) {
      const { data } = registerResponse
      identifySegmentUser(data.user)
      trackMemberRegistration({ promoCode: formData.promoCode, loginType: 'password' }, getSegmentUserContext(data.user))
      trackPromoCodeRedeemed({
        promo: data.promoCodeCredit?.name,
        promoAmount: data.promoCodeCredit?.amount
      })
      setUserToken(data.jwtToken)
      dispatch(setIsAuthorized(true))
      fetchUserContext()
      isShutdownCreateAccountEnabled
        ? navigate(ROUTES.LIMITED_ACCESS)
        : navigate(ROUTES.PRODUCT_TOURS.WELCOME)
    } else {
      !isReCaptchaV3Enabled && onRecaptchaReset()
    }
  }, [
    registerUser,
    fetchUserContext,
    isPrimeGuaranteeEnabled,
    trackMemberRegistration,
    trackPromoCodeRedeemed,
    dispatch,
    navigate,
    onRecaptchaReset,
    isShutdownCreateAccountEnabled
  ])

  const emailErrorMessage = registerUserApiResult.error?.data?.errors?.[0]?.message

  const email = watch('email')

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-4">
        <div className="flex items-center justify-center">
          <div className="w-[300px] flex flex-col">
            <SignInWithGoogleButton
              promoCode={promoCode}
              type={GOOGLE_AUTH_TYPES.SIGN_UP}
            />
            { isAppleSignInEnabled ? (
              <SignInWithAppleButton
                promoCode={promoCode}
                type={APPLE_AUTH_TYPES.SIGN_UP}
                buttonClassName="w-[300px]"
              />
            ) : null }
          </div>
        </div>
        <div className="flex items-center md:my-5 my-6">
          <span className="border border-moonrock basis-1/2" />
          <span className="mx-4">
            Or
          </span>
          <span className="border border-moonrock basis-1/2" />
        </div>
      </div>
      <div className="mb-2">
        <FormEmailField
          control={control}
          name="email"
          value={email}
          label="Email address"
          className="mb-2"
          error={emailErrorMessage ? (
            <div
              className="flex justify-start"
              data-testid="stellar-sign-up-form-email-error"
            >
              {emailErrorMessage}
            </div>
          ) : null}
          setValue={setValue}
        />
      </div>
      <div className="mb-2">
        <FormPasswordField
          control={control}
          name="password"
          defaultValue=""
          hasError={Boolean(errors?.password?.message)}
        />
      </div>
      <FormTextField
        control={control}
        name="promoCode"
        label="Promo code (optional)"
        className="mb-6"
        defaultValue={promoCode}
        inputProps={{
          className: classNames('!font-bold'),
        }}
        helperText={promoCode === WELCOME5_PROMOCODE ? (
          <span className="text-xs text-deepspace-300 font-medium">
            Here&apos;s $5 on us as a thanks for signing up!
          </span>
        ) : ''}
      />
      {isReCaptchaV3Enabled ? (
        <CTAButton
          type="submit"
          variant="contained"
          size="xl"
          className="g-recaptcha w-full text-xl py-4 px-10"
          disabled={!isValid || isSubmitting}
          data-sitekey={CONFIG.RECAPTCHA_V3_SITE_KEY}
          data-callback={handleSubmit}
          data-action="submit"
        >
          {submitBtnText || 'Get started'}
        </CTAButton>
      ) : (
        <>
          {ReCAPTCHA}
          <CTAButton
            type="submit"
            variant="contained"
            size="xl"
            className="g-recaptcha w-full text-xl py-4 px-10"
            disabled={!isValid || isSubmitting}
          >
            {submitBtnText || 'Get started'}
          </CTAButton>
        </>
      )}
      <div>
        <Link
          to={ROUTES.LOGIN}
          size="md"
          className="w-full no-underline my-2 md:mt-6 text-center"
        >
          Already have an account?
        </Link>
      </div>
      <p className="text-xs text-center text-black">
        By joining, I agree to the StellarFi
        <Link
          href="https://www.stellarfi.com/legal/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="mx-1.5 text-xs"
          size=""
        >
          Privacy Policy
        </Link>
        and
        <Link
          href="https://www.stellarfi.com/legal/stellar-terms-and-conditions"
          target="_blank"
          rel="noreferrer"
          className="text-xs"
          size=""
        >
            &nbsp;Terms of Use
        </Link>
        .
      </p>
      <ReCaptchaV3Terms />
    </form>
  )
}

SignUpV3RegistrationForm.propTypes = {
  promoCode: PropTypes.string,
  submitBtnText: PropTypes.string,
}

SignUpV3RegistrationForm.defaultProps = {
  promoCode: null,
  submitBtnText: '',
}

const Memoized = React.memo(SignUpV3RegistrationForm)
export default Memoized
